import { Box, Grid } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { useConfirmationDialog } from "../../../../contexts/ConfirmationDialogContext";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useMessage from "../../../../hooks/useMessage";
import { fetchCustomerOrgById } from "../../../customer/customerOrganizationSlice";
import { fetchWorkLocationsAndContractsByOrgId } from "../../../customer/customerWorkLocationsSlice";
import { NEW_ORDER_COLUMN_WIDTHS } from "../../constants";
import { generateOrderIdempotencyKey } from "../../orderIdempotencyKey";
import { getStoredIdempotencyHash, storeIdempotencyHash } from "../../orderIdempotencyStorage";
import { addNewOrder } from "../../orderRowsSlice";
import {
  FormattedCustomerCompany,
  OrderPreparedPayload,
} from "../../orderTypes";
import { OrderFormShortCard } from "../../styles";
import OrderFormShortButtons from "./OrderFormShortButtons";
import OrderFormShortHeaderRow from "./OrderFormShortHeaderRow";
import OrderFormShortRow from "./OrderFormShortRow";
import * as orderRowHandlers from "./orderFormShortRowHandlers";
import { hasManuallyOfferedRows } from "./orderFormShortRowHandlers";
import { ShiftOrderContext } from "./OrderShortDataProvider";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";

type Props = {
  title: string;
  customerCompany: FormattedCustomerCompany;
  handleSetOrderRowCount: (count: number) => void;
};

const OrderFormShort = ({
  title,
  customerCompany,
  handleSetOrderRowCount,
}: Props) => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { orderRows, validationErrors, bundling } =
    useContext(ShiftOrderContext);

  const dispatch = useAppDispatch();

  const { status: workLocationsStatus } = useAppSelector(
    (state) => state.customerWorkLocations,
  );
  const { status: customerOrganizationStatus } = useAppSelector(
    (state) => state.customerOrganization,
  );
  const { status: orderTemplatesStatus } = useAppSelector(
    (state) => state.orderTemplates,
  );
  const { status: orderRowsStatus } = useAppSelector(
    (state) => state.orderRows,
  );

  const isLoading = [
    workLocationsStatus,
    customerOrganizationStatus,
    orderTemplatesStatus,
    orderRowsStatus,
  ].some((status) => status === "pending");

  const navigate = useNavigate();
  const { sendMessage } = useMessage();
  const { t } = useTranslation(["common", "languages"]);
  const { confirm } = useConfirmationDialog();

  const COMMAND = "addOrder";

  const headers = [
    {
      label: "bundling",
      width: NEW_ORDER_COLUMN_WIDTHS.bundling,
      visibility: "hidden",
    },
    {
      label: "fillShift",
      width: NEW_ORDER_COLUMN_WIDTHS.fillShift,
      visibility: "hidden",
    },
    ...(orderRows.length && hasManuallyOfferedRows(orderRows)
      ? [
          {
            label: "manualOffer",
            width: NEW_ORDER_COLUMN_WIDTHS.manualOffer,
            visibility: "hidden",
          },
        ]
      : []),
    { label: "workLocation", width: NEW_ORDER_COLUMN_WIDTHS.workLocation },
    { label: "orderTemplate", width: NEW_ORDER_COLUMN_WIDTHS.orderTemplate },
    {
      label: "amount",
      width: NEW_ORDER_COLUMN_WIDTHS.amount,
      textAlign: "center",
    },
    { label: "date", width: NEW_ORDER_COLUMN_WIDTHS.date, textAlign: "center" },
    { label: "time", width: NEW_ORDER_COLUMN_WIDTHS.time, textAlign: "center" },
    {
      label: "customerCostCenter",
      width: NEW_ORDER_COLUMN_WIDTHS.customerCostCenter,
    },
    {
      label: "reasonForTemporaryEmployment",
      width: NEW_ORDER_COLUMN_WIDTHS.reasonForTemporaryEmployment,
    },
    {
      label: "more",
      width: NEW_ORDER_COLUMN_WIDTHS.more,
      visibility: "hidden",
    },
    {
      label: "notification",
      width: NEW_ORDER_COLUMN_WIDTHS.notification,
      visibility: "hidden",
    },
  ];

  useEffect(() => {
    const countWithoutEmptyBundles = orderRows.filter(
      (row) => !!row.rows.length,
    );
    handleSetOrderRowCount(countWithoutEmptyBundles.length);
  }, [orderRows.length, handleSetOrderRowCount, orderRows]);

  useEffect(() => {
    if (customerCompany.orgId) {
      dispatch(
        fetchWorkLocationsAndContractsByOrgId({ id: customerCompany.orgId }),
      );
    }
  }, [customerCompany.orgId, dispatch]);

  useEffect(() => {
    if (customerCompany.orgId) {
      dispatch(fetchCustomerOrgById({ id: customerCompany.orgId }));
    }
  }, [customerCompany.orgId, dispatch]);

  const handleSubmit = async () => {
    const data = {
      id: nanoid(),
      title,
      customerCompany,
      orderRows: orderRows,
    };
    const formattedData: OrderPreparedPayload =
      orderRowHandlers.formatData(data);

    const hash = await generateOrderIdempotencyKey(formattedData.orderRows);

    const stored = getStoredIdempotencyHash(COMMAND);
    if (stored === hash) {
      const confirmed = await confirm({
        title: t("DUPLICATE_ORDER_TITLE"),
        contentText: t("DUPLICATE_ORDER_CONTENT"),
        confirmText: t("DUPLICATE_ORDER_CONFIRM"),
        cancelText: t("cancel"),
      });
      if (!confirmed) return;
    }

    const idempotencyKey = stored === hash ? undefined : hash;
    const action = await dispatch(
      addNewOrder({ order: formattedData, idempotencyKey }),
    );

    if (isFulfilled(action)) {
      storeIdempotencyHash(COMMAND, hash);
      setIsSubmitted(true);
      orderRowHandlers.handleLocalStorage(customerCompany);
      sendMessage(t("ORDER_SENT"), "success");
    }
  };

  const hasEmptyValues = orderRows
    .flatMap((row) =>
      row.rows.flatMap(
        // destruct optional fields to exclude them from validation
        ({
          id,
          createdAt,
          descriptionToEezyAndCustomer,
          descriptionToEezyAndCustomerAndTalent,
          isFillShift,
          automatching,
          flexibleStartUntil,
          notifyWhenFulfilled,
          notifyMinutesBeforeIfNotFulfilled,
          ...fields
        }) => Object.values(fields),
      ),
    )
    .some((value: string | number | AutocompleteType | null | Date | boolean) =>
      orderRowHandlers.isFieldValueEmpty(value),
    );

  const hasEmptyBundleContainer = orderRows.find((row) => isEmpty(row.rows));
  const isDirty = !isEmpty(orderRows) && !isSubmitted;

  const submitDisabled =
    hasEmptyValues ||
    isEmpty(orderRows) ||
    (!isEmpty(orderRows) && !!hasEmptyBundleContainer) ||
    !isEmpty(validationErrors) ||
    bundling.show;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <UnsavedChangesProvider
        isDirty={isDirty}
        onSuccessNavigate={() => navigate("/home/orders")}
        isSubmitted={isSubmitted}
      >
        <OrderFormShortCard>
          {!!orderRows.length && (
            <Grid container spacing={1} sx={{ overflow: "visible" }}>
              <OverlaySpinner isLoading={isLoading} />
              <OrderFormShortHeaderRow
                isBundling={bundling.show}
                headers={headers}
              />
              <OrderFormShortRow />
            </Grid>
          )}
          <OrderFormShortButtons />
        </OrderFormShortCard>
        <CustomButton
          variant="contained"
          sx={{ borderRadius: "20px", fontSize: "16px", width: "145px" }}
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          {t("order:sendOrder")}
        </CustomButton>
      </UnsavedChangesProvider>
    </Box>
  );
};

export default OrderFormShort;
